import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { filter, take } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BackButtonBlocker } from './core/navigation/back-button-blocker';
import { NotificationService } from './shared/services/notification-service';
import { AuthService } from './core/auth/auth-service';
import { generateClient } from '@aws-amplify/api';
import { CommonModule } from '@angular/common';
import { NotificationPopup } from './shared/components/notification-popup/notification-popup';

import { OwnershipCoordinatorService } from './shared/services/ownership-coordinator.service';
import { ViewFlightInfoService } from './shared/services/view-flight-info-service';
import { buildFriendlyError } from './shared/services/product-service';
import { SessionTimeoutService } from './shared/services/session-timeout.service';


@Component({
  selector: 'app-root',
  imports: [RouterOutlet, CommonModule, NotificationPopup],
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App implements OnInit, OnDestroy {
  showPopup = false;
  notification: any;
  popupLoading = false;
  selectedAction = '';
  popupError = '';
  constructor(
    private readonly backBlocker: BackButtonBlocker,
    private readonly msal: MsalService,
    private readonly broadcast: MsalBroadcastService,
    private readonly destroyRef: DestroyRef,
    private readonly sessionTimeoutService: SessionTimeoutService,
    private readonly notif: NotificationService,
    private readonly auth: AuthService,
    private readonly ownershipCoordinator: OwnershipCoordinatorService,
    private readonly viewInfoService: ViewFlightInfoService
  ) {}

  ngOnInit(): void {
    this.backBlocker.enableBlocker();
    this.sessionTimeoutService.startWatching();
    void this.initializeAuth().then(async () => {
      const accounts = this.msal.instance.getAllAccounts();

      if (!this.msal.instance.getActiveAccount() && accounts.length) {
        this.msal.instance.setActiveAccount(accounts[0]);
      }
      const account = this.msal.instance.getActiveAccount() ?? accounts[0];
    
      if (!account) {
        this.msal.loginRedirect();
        return;
      }

      const token = await this.auth.getToken();

     

      if (!token) {
        return;
      }

      void this.notif.start();
    });

    this.notif.notification$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
    

      this.showPopup = false;
      this.notification = null;

      this.notification = {
        ...data,
        receivedAt: Date.now(),
      };

      this.popupLoading = false;
      this.selectedAction = '';
      this.popupError = '';

      this.showPopup = true;
    });
  }

  async handleAction(action: string) {
    this.popupLoading = true;
    this.selectedAction = action;
    this.popupError = '';
    const client = generateClient();
    const token = await this.auth.getToken();
    const actedByUserId = this.auth.getUserNid();

    if (!token || !actedByUserId || !this.notification?.requestId) {
      return;
    }
    if (!this.notification) {
      return;
    }
    if (action === 'ACCEPT') {
      this.popupLoading = true;
      this.selectedAction = action;
      this.popupError = '';

      this.ownershipCoordinator.cfhCompleted$.pipe(take(1)).subscribe({
        next: () => {
          this.popupLoading = false;
          this.selectedAction = '';
          this.showPopup = false;
        },
        error: (err) => {
          this.popupLoading = false;
          this.popupError = buildFriendlyError(err) || 'Failed to refresh flight information.';
        },
      });

      this.ownershipCoordinator.triggerCfhReload();

      return;
    }

    if (action === 'REJECT') {
      const currentOwner = this.auth.getUserNid();

      if (!currentOwner) {
        this.popupError = 'Current owner not found';
        this.popupLoading = false;
        return;
      }

      const flightDateTime = this.notification?.flightDateTime;

      if (!flightDateTime) {
        this.popupError = 'Flight date/time missing';
        this.popupLoading = false;
        return;
      }

      const [date, time] = flightDateTime.split('T');

      const request = {
        origin: this.notification.origin,
        date,
        time,
        commodity: this.notification.commodityGroupCode,
        currentOwner,
        action: 'REJECT',
      };

      this.viewInfoService
        .takeOwnership(this.notification.carrierCode, this.notification.flightNumber, request)
        .subscribe({
          next: () => {
            this.ownershipCoordinator.triggerCfhReload();

            this.popupLoading = false;
            this.selectedAction = '';
            this.popupError = '';
            this.showPopup = false;
          },
          error: (err) => {
            this.popupLoading = false;
            this.popupError =
              buildFriendlyError(err) ||
              err?.error?.message ||
              err?.message ||
              'Failed to process rejection request.';
          },
        });

      return;
    }
    try {
      await client.graphql({
        query: `
          mutation Respond(
            $requestId: String!
            $action: String!
            $actedByUserId: String!
          ) {
            respondToOwnershipRequest(
              requestId: $requestId
              action: $action
              actedByUserId: $actedByUserId
            ) {
              requestId
              status
              action
            }
          }
        `,
        variables: {
          requestId: this.notification.requestId,
          actedByUserId,
          action,
        },
        authMode: 'oidc',
        authToken: token,
      });

      this.showPopup = false;
    } catch (err: any) {
      this.popupError =
        buildFriendlyError(err) ||
        err?.errors?.[0]?.message ||
        err?.message ||
        'Failed to submit response.';
    } finally {
      this.popupLoading = false;
    }
  }
  private handleAllow(): void {
    this.ownershipCoordinator.triggerCfhReload();
  }
  private handleReject(): void {
    const currentOwner = this.auth.getUserNid();

    if (!currentOwner) {
      return;
    }

    const flightDateTime = this.notification?.flightDateTime;

    if (!flightDateTime) {
      return;
    }

    const [date, time] = flightDateTime.split('T');

    const request = {
      origin: this.notification.origin,
      date,
      time,
      commodity: this.notification.commodityGroupCode,
      currentOwner,
      action: 'REJECT',
    };

    this.viewInfoService
      .takeOwnership(this.notification.carrierCode, this.notification.flightNumber, request)
      .subscribe({
        next: () => {
          this.ownershipCoordinator.triggerCfhReload();
        },
        error: () => {},
      });
  }
  ngOnDestroy(): void {
    this.backBlocker.disableBlocker();
  }

  private async initializeAuth(): Promise<void> {
   

   
    let accounts = this.msal.instance.getAllAccounts();

    if (accounts.length && !this.msal.instance.getActiveAccount()) {
      this.msal.instance.setActiveAccount(accounts[0]);
    }

    this.broadcast.msalSubject$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((msg: EventMessage) => {
        if (msg.eventType === EventType.LOGIN_SUCCESS) {
          const result = msg.payload as AuthenticationResult;

          if (result?.account) {
            this.msal.instance.setActiveAccount(result.account);
          }

          this.notif.stop();
          void this.notif.start();
        }

        if (msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
          // console.log('[MSAL] TOKEN REFRESHED');
        }
      });

    await firstValueFrom(
      this.broadcast.inProgress$.pipe(filter((status) => status === InteractionStatus.None))
    );
    console.timeEnd('MSAL Initialization');
    accounts = this.msal.instance.getAllAccounts();
    if (!this.msal.instance.getActiveAccount() && accounts.length) {
      this.msal.instance.setActiveAccount(accounts[0]);
    }
  }
}
